import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { formatDayLabel, today } from '../api/calendar'
import { describeClass } from '../api/classDescription'
import { raiseAsk } from '../api/asks'
import { excuseAbsence, withdrawExcusal } from '../api/reportedAbsences'
import {
  nothingCountedYet,
  shortDate,
  standingCaveat,
  standingSummary,
  type AttendanceStanding,
  type ClassAttendance,
  type DancerStanding,
  type MissedSession,
  type OverallState,
} from '../api/portal'

/**
 * One attendance panel, three doors (D-589).
 *
 * The parent's Home, the owner's dancer record and a teacher's view of the
 * same dancer all render this. What differs is who is looking: a parent gets
 * the repair controls on each missed night and the makeup offer at the foot,
 * the studio gets the same nights as facts and its own actions. The rows are
 * the same rows because they come from the same list.
 *
 * **The overall is the only verdict that gates** (PRD §7.6). Each class line
 * carries its own verdict behind the floor (D-587) as a prompt to look, and
 * the panel's one colour says which case this is (D-588): grey until there is
 * enough to judge, green, amber when a class is under, red when she is.
 *
 * **Makeups live under the class they repair.** The separate "3 missed" list
 * is gone: a night sits under its class with what is happening about it on
 * the same row, and a made-up night stays visible and says so - where it used
 * to vanish and leave the percentage unexplained.
 */

export type AttendanceViewer = 'parent' | 'staff'

export type AttendancePanelProps = {
  standing: DancerStanding
  missed: MissedSession[]
  viewer: AttendanceViewer
  /**
   * What a parent can do about one absence - the makeup finder or the private
   * request - rendered under the row. Only for a night with no repair yet; the
   * panel never asks for one on a night that is booked, requested or made up.
   */
  renderRepair?: (night: MissedSession) => ReactNode
  /** The foot of the panel: the makeup offer for a parent, the studio's buttons for staff. */
  footer?: ReactNode
  /** The studio's word for "makeups are not offered for this class" (D-508). */
  notOffered?: string
}

/** The panel's one colour, as the tone the badges already use. */
export function toneOf(state: OverallState | undefined, standing: AttendanceStanding): 'good' | 'warn' | 'crit' | 'mute' {
  switch (state ?? deriveState(standing)) {
    case 'Under':
      return 'crit'
    case 'ClassUnder':
      return 'warn'
    case 'Good':
      return 'good'
    default:
      return 'mute'
  }
}

/**
 * What the server says, or what an older payload implied (D-588).
 *
 * The state is derived on the server so no screen derives its own; this is
 * the fallback for a response that predates it, and it can only say green,
 * red or grey - never amber, which needs the class rows the server has.
 */
function deriveState(standing: AttendanceStanding): OverallState {
  if (standing.verdict) return standing.verdict === 'Under' ? 'Under' : standing.verdict === 'Good' ? 'Good' : 'NotYet'
  if (standing.percentage === null || !standing.isGated) return 'NotYet'
  return standing.meetsThreshold ? 'Good' : 'Under'
}

/**
 * The sentence under the figure (D-588). The rule, not the shortfall (D-388):
 * "needs 80% or above" cannot be read backwards the way "below 80%" could.
 */
export function stateSentence(standing: DancerStanding): string {
  const { overall } = standing
  const state = standing.state ?? deriveState(overall)

  if (!overall.isGated) return standingSummary(overall)
  if (overall.percentage === null) return standingSummary(overall)

  switch (state) {
    case 'NotYet':
      return 'Too early to say'
    case 'Under':
      return `Needs ${overall.threshold}% or above`
    case 'ClassUnder': {
      const n = standing.classesUnder?.length ?? 0
      return `Above ${overall.threshold}% overall — ${n === 1 ? '1 class is' : `${n} classes are`} under`
    }
    default:
      return `Above the ${overall.threshold}% required`
  }
}

/**
 * A class name a parent can read (D-581): the parsed label, with the person
 * it belongs to when it is a solo. A name the parser cannot read comes back
 * as it is.
 */
export function readable(className: string): string {
  const { label, who } = describeClass(className)
  return who ? `${label} · ${who}` : label
}

/** The studio's word for each kind of class on a row (D-227). */
const CATEGORY_WORD = { Tech: 'technique', Choreo: 'choreo', Recital: 'recital' } as const

/**
 * Whether a makeup deadline has already gone (D-655).
 *
 * <b>`today()` and never `new Date()`</b> (D-485): the second is the UTC date,
 * which is tomorrow after about 7pm Central - inside studio hours - so a
 * deadline would read as passed on the evening before it actually is, on every
 * class night and only in production.
 */
export function pastDue(makeUpBy: string): boolean {
  return makeUpBy < today()
}

/*
 * ⚠ **A `bandWords` lived here for about ten minutes and was deleted** (D-934).
 *
 * It split the stored `2/3.5` on slashes and printed the first and last
 * pieces — which is wrong for the named ladder, because `LevelBand` sorts its
 * rungs ordinally and stores `ADV · BEG · INTERM`. The ends are the *middle*
 * and the *bottom*, so it would have read *levels ADV to INTERM*.
 *
 * `LevelBand.ToWords` knows that and is the one formatter now, so both
 * readers — this line and the banked panel's row — are given the sentence
 * rather than the storage.
 */

/**
 * <b>A night with a deadline still running</b> (D-744, found by the audit).
 *
 * <b>Never held back, whatever the class percentage says.</b> A company dancer's
 * unrepaired absence sits in the repair window and is kept <i>out of the
 * denominator</i> while it runs (D-654), so her class reads 9 of 9 and the gate
 * below would hide the private - on a row that is at the same moment printing
 * <i>Make up by 16 Oct</i> in amber with nothing beside it to press. When the
 * window closes the absence starts counting, the class turns under, the offer
 * appears, and the row now reads <i>Too late to make up</i>.
 *
 * That is the exact failure D-652 and D-654 were written against: <i>a dancer
 * reading 100% with a deadline running out and nothing telling her</i>. The gate
 * is about not pestering a family over one absence that costs her nothing, and a
 * clock running is the case where it costs her everything.
 */
function onAClock(night: MissedSession): boolean {
  return night.makeUpBy != null && !pastDue(night.makeUpBy)
}

/** "2026-09-15" -> "Tue 15 Sep": the form a missed night is dated in. */
export function shortDay(iso: string): string {
  const { weekday, day, month } = formatDayLabel(iso)
  return `${weekday} ${day} ${month}`
}

/**
 * The one line that says what is owed, so the fold can stay shut on a green
 * night and still be honest: "2 to make up · 1 booked · 1 made up".
 */
/**
 * The kinds of repair the line below names one by one.
 *
 * Listed rather than derived, so a seventh kind is silent here until somebody
 * gives it words — the same direction {@link owedLine} already takes.
 */
const REPORTED_REPAIRS = ['Requested', 'Booked', 'MadeUp', 'Banked', 'PrivateLesson', 'Spent']

/**
 * <b>The absences the line actually mentions</b> (D-758, found by the audit).
 *
 * <b>It is not every absence, which is the trap.</b> An unrepaired night is
 * named only when it is <i>repairable</i> — so a recital night, which the
 * studio asks nobody to make up, appears in no part of the line at all
 * (`MakeupRepairPath.Repairable` is false for recital and for a class Tonya
 * has excused).
 *
 * That matters because the same two classes are the ones outside the
 * percentage: `Repairable` and `CompetitionAttendance.Counts` are false
 * <i>together</i> for recital and for `NoMakeup`. A count taken over a wider
 * population than the line's would therefore say <i>1 of those</i> about a
 * night the line never mentioned — and the only nights it could be pointing
 * at are ones that <b>are</b> in the percentage. D-758's own failure,
 * backwards.
 *
 * So both read this, and cannot drift (A-17).
 */
function reported(missed: MissedSession[]): MissedSession[] {
  return missed.filter((m) => {
    if (m.state === 'Excused') return false

    const kind = m.repair?.kind ?? 'None'

    return kind === 'None' ? m.repairable : REPORTED_REPAIRS.includes(kind)
  })
}

export function owedLine(
  missed: MissedSession[],
  standing: AttendanceStanding,
  { withCaveat = true }: { withCaveat?: boolean } = {},
): string | null {
  const absences = reported(missed)
  const kind = (k: string) => absences.filter((m) => (m.repair?.kind ?? 'None') === k).length

  const toRepair = absences.filter((m) => (m.repair?.kind ?? 'None') === 'None').length
  const parts: string[] = []

  if (toRepair) parts.push(`${toRepair} to make up`)
  if (kind('Requested')) parts.push(`${kind('Requested')} private requested`)
  if (kind('Booked')) parts.push(`${kind('Booked')} booked`)
  if (kind('MadeUp')) parts.push(`${kind('MadeUp')} made up`)
  // Counted separately from "made up" (D-592): she booked none of these, and
  // a summary that folded them in would overstate what she arranged.
  if (kind('Banked')) parts.push(`${kind('Banked')} covered by a banked class`)
  // A private lesson she was marked present at (D-729): arranged, but not a
  // makeup she booked against this night, so its own words too.
  if (kind('PrivateLesson')) parts.push(`${kind('PrivateLesson')} covered by a private lesson`)
  if (kind('Spent')) parts.push(`${kind('Spent')} ${kind('Spent') === 1 ? 'makeup' : 'makeups'} missed`)

  // The unmarked caveat rides here once there is a figure; before there is,
  // the nothing-counted sentence already carries it, and driven in a browser
  // the panel said "5 classes not yet marked" twice, one line apart.
  const caveat = withCaveat ? standingCaveat(standing) : null
  if (caveat) parts.push(caveat)

  return parts.length ? parts.join(' · ') : null
}

/**
 * <b>How many of the things owed above are not in the percentage</b> (D-758).
 *
 * `asked`: *"looks like it is counting choreo in the 80% and it should only be
 * tech."* It is not - D-644's fold is right, and `observed` on the reported
 * screen the figure lands exactly on the technique-only arithmetic. What was
 * wrong is that **the line saying what is owed counts every absence**, while
 * the percentage immediately above it counts technique only. A parent reads
 * *66.7%* and *2 to make up* one under the other and concludes the second
 * explains the first.
 *
 * D-644 named this failure in its own words and then left this line out of the
 * remedy: *a total that silently disagrees with its own breakdown is worse than
 * no total* (D-209). The rows were marked `Not counted toward competing`; the
 * summary over them was not.
 *
 * <b>It counts the absences `owedLine` prints and no others</b> - both read
 * {@link reported}, so *of those* is true of the sentence it sits under. The
 * first version filtered on `state !== 'Excused'` alone and an audit caught
 * it: a recital night is outside the percentage <i>and</i> outside the line,
 * so counting it made the sentence point at whatever the line did mention -
 * which is only ever something the percentage includes.
 *
 * <b>A group answers for its members</b> (D-170): a night in any alternative
 * belongs to the group's row, and that row's own flag decides - the same
 * mapping `AttendanceService` uses for the figure itself, so the two cannot
 * disagree about one night.
 *
 * <b>A floor rather than a total.</b> A night whose class has no row at all -
 * the orphan case the list below renders under its own name - is left out. It
 * is outside the percentage too (nothing without a row is in the standing), so
 * the sentence can undercount and never overcount, which is the safe
 * direction for a number that tells somebody she has less to do.
 *
 * <b>Absent means counting</b> (`!== false`): an older payload that does not
 * carry the flag says nothing rather than something wrong.
 */
export function notCountedAmong(
  missed: MissedSession[],
  byClass: ClassAttendance[],
): number {
  const notCounting = new Set<string>()

  for (const row of byClass) {
    if (row.countsTowardEligibility !== false) continue

    notCounting.add(row.studioClassId)
    for (const alternative of row.alternatives) notCounting.add(alternative.studioClassId)
  }

  return reported(missed).filter(
    (m) => m.studioClassId != null && notCounting.has(m.studioClassId),
  ).length
}

const TONE_TEXT = { good: 'text-good', warn: 'text-warn', crit: 'text-crit', mute: 'text-ink-3' } as const
const TONE_BAR = { good: 'bg-good', warn: 'bg-warn', crit: 'bg-crit', mute: 'bg-ink-3' } as const

export default function AttendancePanel({
  standing,
  missed,
  viewer,
  renderRepair,
  footer,
  notOffered = 'Makeups are not offered for this class.',
}: AttendancePanelProps) {
  const { overall, byClass } = standing
  const state = standing.state ?? deriveState(overall)
  const tone = toneOf(state, overall)

  /*
   * Open when something needs looking at, shut when it is only history
   * (D-341). A verdict against her, or a class under the line, is a reason
   * to look; so is an absence nothing is repairing yet, because leaving the
   * repair behind a tap makes it deeper than the list this replaced - and an
   * absence nothing *can* repair still has to be seen, with the sentence that
   * says why (D-508). A recorded excuse, a booked makeup, a night already
   * made up are settled, and a green panel over settled history stays folded.
   */
  const needsHer = missed.some((m) => m.state !== 'Excused' && (m.repair?.kind ?? 'None') === 'None')
  const [open, setOpen] = useState<boolean | null>(null)
  const isOpen = open ?? (state === 'Under' || state === 'ClassUnder' || needsHer)

  /*
   * A panel that says why, rather than no panel at all (D-311): nothing
   * counted yet is the ordinary case in September, and a rec dancer is
   * measured but never gated, so neither gets a percentage, a bar or a
   * verdict - just the sentence, the nights, and the foot.
   */
  const nothingYet = overall.percentage === null

  const owed = owedLine(missed, overall, { withCaveat: !nothingYet })
  const notCounted = notCountedAmong(missed, byClass)

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5" data-state={state}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Attendance</span>
        {!nothingYet && (
          <span className={`text-[22px] font-bold tabular ${TONE_TEXT[tone]}`}>{overall.percentage}%</span>
        )}
      </div>

      {overall.isGated && !nothingYet && (
        <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-surface-3">
          <div
            className={`h-full rounded-full ${TONE_BAR[tone]}`}
            style={{ width: `${Math.min(100, overall.percentage ?? 0)}%` }}
          />
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2 text-[12px]">
        <span className={`font-semibold ${nothingYet ? 'text-ink-2' : TONE_TEXT[tone]}`}>
          {nothingYet ? nothingCountedYet(overall) : stateSentence(standing)}
        </span>

        {/* From the server, never typed here (A-16, D-168); the grace week
            named beside it (D-591). */}
        {overall.isGated && (
          <span className="text-ink-3 tabular">
            {overall.threshold}% required
            {overall.countsFrom && <> &middot; from {shortDate(overall.countsFrom)}</>}
          </span>
        )}
      </div>

      {/*
        **A deadline running out, said out loud** (D-654).

        An absence she still has time to repair is out of the figures above, so
        she reads 100% while a clock runs. That is the rule Tonya asked for —
        and it is also exactly the silent failure D-652's immediate verdict was
        built to prevent, arriving through the other door. A number nobody is
        warned about falling is worse than one that was never flattering.

        `warn`, not `crit`: nothing has gone wrong yet. It is a thing to do.
      */}
      {(overall.pendingRepair ?? 0) > 0 && (
        <p className="mt-1.5 border-l-2 border-warn pl-2 text-[12px] text-ink-2">
          {overall.pendingRepair === 1
            ? '1 missed class is not counted yet, because there is still time to make it up.'
            : `${overall.pendingRepair} missed classes are not counted yet, because there is still time to make them up.`}{' '}
          They count against her if they are not made up in time.
        </p>
      )}

      {state === 'ClassUnder' && (standing.classesUnder?.length ?? 0) > 0 && (
        <p className="mt-1.5 border-l-2 border-warn pl-2 text-[12px] text-ink-2">
          {standing.classesUnder!.join(', ')} &mdash; see below. This does not affect her eligibility on its
          own.
        </p>
      )}

      {owed && <p className="mt-1.5 text-[12px] text-ink-3">{owed}</p>}

      {/*
        <b>Which of those are not in the number above</b> (D-758).

        The line before this counts every absence; the percentage counts
        technique only (D-644). Read one under the other they look like cause
        and effect, and that is how a correct figure got reported as wrong.

        <b>Only where there is a figure to be outside of</b>, and an audit had
        to correct what that means. <i>Gated</i> is not the same as <i>has a
        percentage</i>: `StandingCalculator` works one out from what was
        counted whatever `isGated` says, and only the verdict is withheld - so
        a rec dancer, who is measured and never judged (D-311), has a number
        and would have been told which of her absences is not counted toward
        competing. She is not competing. Both conditions, therefore: something
        to be outside of, and a reader it means anything to.
      */}
      {!nothingYet && overall.isGated && notCounted > 0 && owed && (
        <p className="mt-1.5 border-l-2 border-line pl-2 text-[12px] text-ink-3">
          {notCounted === 1
            ? '1 of those is a class that is not counted toward competing, so it does not change the percentage above.'
            : `${notCounted} of those are classes that are not counted toward competing, so they do not change the percentage above.`}
        </p>
      )}

      {(byClass.length > 0 || missed.length > 0) && (
        <div className="mt-3 border-t border-line pt-2.5">
          <button
            type="button"
            onClick={() => setOpen(!isOpen)}
            aria-expanded={isOpen}
            className="flex w-full items-center justify-between gap-2 py-1 text-left text-[12.5px] font-semibold text-ink-2"
          >
            <span>By class</span>
            <ChevronDown
              size={15}
              strokeWidth={2.5}
              className={`shrink-0 text-ink-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && (
            <ClassLines
              standing={standing}
              missed={missed}
              viewer={viewer}
              renderRepair={renderRepair}
              notOffered={notOffered}
            />
          )}
        </div>
      )}

      {footer && <div className="mt-3">{footer}</div>}
    </section>
  )
}

/** The classes, each with the nights she missed from it underneath. */
function ClassLines({
  standing,
  missed,
  viewer,
  renderRepair,
  notOffered,
}: {
  standing: DancerStanding
  missed: MissedSession[]
  viewer: AttendanceViewer
  renderRepair?: (night: MissedSession) => ReactNode
  notOffered: string
}) {
  const { overall, byClass } = standing

  /*
   * A night belongs to the row that speaks for its class - for a group row,
   * any of the alternatives (D-170). A night whose class has no row (a class
   * she has since left) is listed last under its own name rather than lost.
   */
  const claimed = new Set<string>()
  const under = (row: ClassAttendance) => {
    const ids = new Set([row.studioClassId, ...row.alternatives.map((a) => a.studioClassId)])
    const mine = missed.filter((m) => m.studioClassId && ids.has(m.studioClassId))
    mine.forEach((m) => claimed.add(m.occurrenceId))
    return mine
  }

  const rows = byClass.map((row) => ({ row, nights: under(row) }))
  const orphans = missed.filter((m) => !claimed.has(m.occurrenceId))

  return (
    <ul className="mt-1.5 flex flex-col gap-2.5">
      {rows.map(({ row, nights }) => (
        <li key={row.studioClassId} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3">
            {/* Read the name (D-581): "Tap technique · Bailey Jo", with the
                machine-shaped Jackrabbit name on hover. */}
            <span className="min-w-0 flex-1 text-[12.5px] text-ink-2 wrap-anywhere" title={row.className}>
              {readable(row.className)}
              {/* Which kind of class (D-227): tech and choreo are the whole
                  point of the breakdown, and two rows both reading "Jazz"
                  said nothing. */}
              {row.category && (
                <span className="ml-1.5 text-[10.5px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
                  {CATEGORY_WORD[row.category]}
                </span>
              )}
              {/*
                <b>Says which rows are in the number above</b> (D-644).

                Technique is what gates competing, so choreography and recital
                are shown and judged but folded out of the total - and the rows
                therefore stop adding up to it. A breakdown that silently
                disagrees with its own headline is worse than no breakdown,
                because the first thing anybody does is add it up (D-209).

                Read from the server's own answer rather than from the category
                beside it, so the two can never drift apart.
              */}
              {row.countsTowardEligibility === false && (
                <span className="mt-0.5 block text-[11px] text-ink-3">
                  Not counted toward competing
                </span>
              )}
              {row.isGroup && (
                <span className="mt-0.5 block text-[11.5px] text-ink-3">
                  Any {row.requiredPerWeek} of {row.alternatives.map((a) => a.name).join(', ')}
                </span>
              )}
            </span>
            <VerdictChip row={row} gated={overall.isGated} />
          </div>

          {nights.length > 0 && (
            <NightRows
              nights={nights}
              viewer={viewer}
              renderRepair={renderRepair}
              notOffered={notOffered}
              classIsUnder={row.verdict === 'Under'}
              /*
                <b>A grouped row has to name the class each night was</b>
                (D-788). The row stands for a choice - "any 1 of A, B" - so
                without this the reader is told she was absent and not from
                what, which is the one thing she needs in order to act. A
                single-class row already says it in the heading above, and
                repeating it on every night there is noise.
              */
              nameTheClass={row.isGroup}
            />
          )}
        </li>
      ))}

      {orphans.map((night) => (
        <li key={night.occurrenceId} className="flex flex-col gap-1">
          <span className="text-[12.5px] text-ink-2 wrap-anywhere" title={night.className}>
            {readable(night.className)}
          </span>
          <NightRows nights={[night]} viewer={viewer} renderRepair={renderRepair} notOffered={notOffered} />
        </li>
      ))}

      {viewer === 'staff' && <HowTheseAreMadeUp missed={missed} />}
    </ul>
  )
}

/**
 * How the outstanding absences on this panel get made up (D-744).
 *
 * <b>Once, at the foot, and only for the kinds she actually has.</b> `asked`,
 * after the per-night version was reported from production as claiming those
 * nights were already repaired: *"Instead of having the 'makeup with a private'
 * on every line, would it be better if a dancer has missed any choreo classes to
 * just show something ... that says like 'Choreo classes can be made up by
 * booking a private' and then same thing for the tech ones ... clean it up and
 * make it easier to read"*.
 *
 * <b>Two sentences rather than one</b>, because a company dancer has both kinds
 * and one line would be wrong for half her list. <b>And only where something is
 * outstanding</b>: advice about a problem she does not have is the noise this
 * removes.
 *
 * <b>Staff only.</b> A parent is handed the action itself instead, which is the
 * better thing to give somebody who can act - `asked`: *"sounds like parents
 * view is currently fine, lets just make this small change to what teachers
 * see"*.
 */
function HowTheseAreMadeUp({ missed }: { missed: MissedSession[] }) {
  const outstanding = missed.filter(
    (m) => m.state !== 'Excused' && (m.repair?.kind ?? 'None') === 'None' && m.repairable,
  )

  const byPrivate = outstanding.some((m) => m.repairedByPrivate)
  const byDropIn = outstanding.some((m) => !m.repairedByPrivate)

  if (!byPrivate && !byDropIn) return null

  return (
    <li className="mt-0.5 flex flex-col gap-0.5 border-t border-line pt-2 text-[12px] text-ink-3">
      {byPrivate && <span>Choreo classes are made up by booking a private.</span>}
      {byDropIn && (
        <span>
          Technique classes are made up by dropping into another class, or at a makeup class the
          family pays the teacher for.
        </span>
      )}
    </li>
  )
}

/**
 * The class's own figure, with its verdict behind the floor (D-587, D-744).
 *
 * <b>The floor is one counted session now</b>, so a row is grey only until
 * something has been counted - it was four, to keep `0 of 1 (0%)` in red from
 * turning every first absence into an alarm, and the studio asked for that to
 * go. A rec dancer's rows are never judged at all.
 */
function VerdictChip({ row, gated }: { row: ClassAttendance; gated: boolean }) {
  const base = 'inline-flex items-baseline gap-1 rounded-lg px-2 py-1 text-[12.5px] font-semibold tabular whitespace-nowrap'

  if (row.counted === 0) {
    return <span className={`${base} bg-surface-2 font-medium text-ink-3`}>nothing counted yet</span>
  }

  const verdict = gated ? (row.verdict ?? 'NotYet') : 'NotYet'

  if (verdict === 'NotYet') {
    return (
      <span className={`${base} bg-surface-2 font-medium text-ink-3`}>
        {row.attended} of {row.counted}
        {gated && <> &middot; too early to say</>}
      </span>
    )
  }

  const colour = verdict === 'Under' ? 'bg-crit-bg text-crit' : 'bg-good-bg text-good'

  return (
    <span className={`${base} ${colour}`}>
      {row.attended} of {row.counted} <small className="font-medium opacity-70">({row.percentage}%)</small>
    </span>
  )
}

/**
 * The nights under a class line, each with its answer on the same row.
 *
 * Six things a row can say, and nothing else: absent with a way to repair it,
 * requested, booked, made up, away, or not offered (D-589). The repair
 * controls are the screen's - a parent's finder and private request, nothing
 * for the studio - but the facts are the same for everyone.
 */
function NightRows({
  nights,
  viewer,
  renderRepair,
  notOffered,
  classIsUnder = true,
  nameTheClass = false,
}: {
  nights: MissedSession[]
  viewer: AttendanceViewer
  renderRepair?: (night: MissedSession) => ReactNode
  notOffered: string
  /** Whether the class these nights belong to is below the threshold (D-744). */
  classIsUnder?: boolean
  /** Say which class each night was — for a row standing for a choice (D-788). */
  nameTheClass?: boolean
}) {

  return (
    <ul className="ml-0.5 flex flex-col gap-1.5 border-l-2 border-surface-3 pl-2.5">
      {nights.map((night) => {
        const repair = night.repair ?? { kind: 'None' as const, on: null, with: null }
        const away = night.state === 'Excused'

        return (
          <li key={night.occurrenceId} className="flex flex-col gap-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-[12px]">
              <span className="min-w-[5.5rem] text-ink-2 tabular">{shortDay(night.date)}</span>
              {nameTheClass && (
                <span className="text-ink-3 wrap-anywhere" title={night.className}>
                  {readable(night.className)}
                </span>
              )}
              <span
                className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                  away ? 'bg-surface-2 text-ink-2' : 'bg-warn-bg text-warn'
                }`}
              >
                {away ? 'Away' : 'Absent'}
              </span>

              {!away && repair.kind === 'MadeUp' && (
                <span className="text-good">
                  &#10003; Made up{repair.on && <> &middot; {shortDay(repair.on)}</>}
                  {repair.with && <>, {repair.with}</>}
                </span>
              )}
              {/*
                A class she took anyway, counted against this night (D-610).
                Its own sentence rather than "Made up", because she did not
                book anything to repair this - and the date beside it is
                *before* the night, which under "Made up" reads as a bug.
              */}
              {!away && repair.kind === 'Banked' && (
                <span className="text-good">
                  &#10003; Covered by a banked class
                  {repair.on && <> &middot; {shortDay(repair.on)}</>}
                  {repair.with && <>, {repair.with}</>}
                </span>
              )}
              {/*
                <b>The other class in her group stood for the week</b> (D-788).
                Nothing was made up and nothing was banked: the studio asked for
                one of two and she went to one of them, so there was never
                anything owing. Before this the row read <i>2 of 2</i> and the
                night under it read <i>Make up by 20 Oct</i>.
              */}
              {!away && repair.kind === 'Paired' && (
                <span className="text-good">
                  &#10003; Covered by {repair.with ?? 'the paired class'}
                  {repair.on && <> &middot; {shortDay(repair.on)}</>}
                </span>
              )}
              {/* A private lesson she was marked present at, covering this night (D-729). */}
              {!away && repair.kind === 'PrivateLesson' && (
                <span className="text-good">
                  &#10003; Covered by a private lesson
                  {repair.with && <> with {repair.with}</>}
                  {repair.on && <> &middot; {shortDay(repair.on)}</>}
                </span>
              )}
              {!away && repair.kind === 'Booked' && (
                <span className="text-ink-2">
                  Booked{repair.on && <> &middot; {shortDay(repair.on)}</>}
                  {repair.with && <>, {repair.with}</>}
                </span>
              )}
              {!away && repair.kind === 'Requested' && (
                <span className="text-ink-2">
                  Private requested{repair.with && <> &middot; waiting on {repair.with}</>}
                </span>
              )}
              {/* The booking is used (D-278), so no link: it would refuse. */}
              {!away && repair.kind === 'Spent' && (
                <span className="text-ink-3">
                  Missed the makeup{repair.on && <> &middot; {shortDay(repair.on)}</>}
                  {repair.with && <>, {repair.with}</>}
                </span>
              )}
              {/*
                **When this one stops being fixable** (D-655).

                `asked`: *"Can a parent see on their attendance as well as the
                dancers account when their pending absence is about to be
                applied to their account?"* Absent for everybody but the
                company team, because only they have a deadline.

                **On the night rather than only in the summary above**, because
                a parent with three pending absences needs to know *which* one
                is running out — a single soonest-date tells her something is
                coming and not what to do about it. The panel is one component
                behind three doors (D-589), so her account, the dancer's own
                and the studio's all say it.
              */}
              {!away && repair.kind === 'None' && night.repairable && night.makeUpBy && (
                <span className={pastDue(night.makeUpBy) ? 'text-ink-3' : 'text-warn'}>
                  {pastDue(night.makeUpBy)
                    ? 'Too late to make up'
                    : `Make up by ${shortDay(night.makeUpBy)}`}
                </span>
              )}

              {!away && repair.kind === 'None' && !night.repairable && (
                <span className="text-ink-3">{notOffered}</span>
              )}
              {/*
                <b>Why a banked class did not cover this night</b> (D-931,
                D-933).

                **The silence this ends.** The panel said *1 ballet technique
                banked* in one box and *make up by 29 Oct* in another, with
                nothing joining them - and the rule refusing the credit was
                right the whole time. It took a production database to answer,
                which is the defect rather than the refusal.

                ⚠ **Studio only, until the teacher can actually be asked**
                (G3). The server leaves `nearlyCovered` off the family's
                payload, so this condition is belt to its braces - and the
                condition is here as well because a reader of this file should
                not have to go and check.

                **Both bands, no computed distance.** The reader does that
                arithmetic; a second opinion in here about how far apart two
                rungs are is the ladder growing a second home.
              */}
              {!away && viewer === 'staff' && night.nearlyCovered && (
                <NearlyCoveredLine nearly={night.nearlyCovered} />
              )}

              {/*
                ⚠ **Forgiving a night nobody told us about** (D-941).

                `asked`: *"We need to allow a way for Tonya to excuse absences
                in situations where its needed even if the dancer didn't mark
                that they were going to miss a class."*

                **The door existed and nothing reached it.** The excuse
                endpoints take any record that is not present, and the only
                screen offering them lists `ReportedAt != null` - so an absence
                nobody reported could be forgiven by the API and by no person.

                **Here rather than on the register**, because this is the list
                of a dancer's absences the studio already reads, and forgiving
                one is a decision about the season rather than about tonight.
              */}
              {viewer === 'staff' && night.absenceRecordId && (
                <ExcuseThisNight
                  recordId={night.absenceRecordId}
                  excused={night.state === 'Excused'}
                />
              )}
              {/*
                <b>How this kind of absence is made up is said once, at the
                foot</b> (D-744). It was a line on every outstanding night
                reading *Repaired by a private* - which is the **route** the
                flag carries (`MakeupRepairPath.ByPrivate`, D-405) written as a
                completed fact, on the one line that renders when nothing has
                repaired it. Reported from production against two company
                dancers whose classes are all choreo, so every absence they had
                said it: *"they have absences, but have been made up by privates
                and makeups, but the top percentage bar doesn't seem to be
                updating"*. The bar was right and the row was lying.
              */}
            </div>

            {/*
              **What the teacher wrote for the family** (D-656). `asked`: *"a
              staff notes section not visible to parents, and a parent notes
              section visible to both."*

              The studio's own note is a different field and never reaches this
              payload at all — hiding it here would leave it in every browser
              that opens the panel, which is not hiding it.

              Shown to staff too, deliberately: "visible to both" is the ask,
              and a teacher covering a class needs to see what the family was
              already told.
            */}
            {night.familyNote && (
              <p className="text-[12px] wrap-anywhere text-ink-2 italic">
                &ldquo;{night.familyNote}&rdquo;
              </p>
            )}

            {/* A parent's controls, only where there is something to do (D-342).
                Left-aligned on purpose: a stretched button centres its own
                text, and driven in a browser the link floated mid-row. */}
            {/*
              <b>A private is offered once the class is actually under</b>
              (D-744). `asked`: *"when a choreo class has been missed, lets not
              show the book a private to make it up until after the dancer has
              fallen below 80% for that class"*. One missed choreo night out of
              ten is not a problem to solve, and a button that says it is asks a
              family to buy a lesson they do not need.
              <b>Only the private route is held back</b> - a technique absence is
              made up by dropping into a class that is already running, which
              costs the family nothing to arrange and is worth taking early.
            */}
            {!away && repair.kind === 'None' && night.repairable && viewer === 'parent'
              && (!night.repairedByPrivate || classIsUnder || onAClock(night)) && (
              <div className="flex flex-col items-start">{renderRepair?.(night)}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Why a banked class did not cover this night, and the one control that can
 * do anything about it (D-931, D-936).
 *
 * **Studio screens only** (G3). The caller gates on the viewer and the server
 * leaves `nearlyCovered` off a family's payload, so this component is never
 * rendered for a parent — but it asks nothing of the family either way.
 *
 * ⚠ **The button is the whole point of slice 2.** Without it the sentence is
 * a diagnosis nobody can act on, which is the half-built shape `CLAUDE.md`
 * rule 2 is written about: the endpoint shipped and the button had not.
 */
/**
 * Forgive one night, or put it back, from the list the studio already reads
 * (D-941).
 *
 * ⚠ **Both directions, because forgiving was a one-way door.** The only screen
 * that could withdraw an excusal lists reported absences, and this control's
 * whole purpose is the *unreported* ones - so a mistaken tap changed a figure
 * that decides who competes, with no way back through the app. An audit caught
 * it before anybody met it.
 *
 * **The capability is checked in two places and that is deliberate.** The panel
 * endpoint omits `absenceRecordId` for anyone without `ExcuseAbsences`, so
 * there is no key; this also asks `useCan`, so the screen states the rule it is
 * obeying rather than relying on a field being absent.
 */
function ExcuseThisNight({
  recordId,
  excused,
}: {
  recordId: string
  excused: boolean
}) {
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'failed'>('idle')
  const [problem, setProblem] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [open, setOpen] = useState(false)

  async function run(what: 'excuse' | 'restore') {
    setState('saving')
    setProblem(null)

    try {
      // The helpers the other excuse screen uses (A-17): one spelling of the
      // call, one wording of the refusal.
      if (what === 'excuse') await excuseAbsence(recordId, note.trim() === '' ? null : note.trim())
      else await withdrawExcusal(recordId)

      setState('done')
    } catch (e) {
      setState('failed')
      setProblem(e instanceof Error ? e.message : 'That could not be saved.')
    }
  }

  if (state === 'done') {
    /*
      Said rather than vanishing: a control that disappears reads as a tap that
      did nothing.

      ⚠ **It does not promise a number will move.** An earlier version said
      *"the figure updates when this reloads"*, which is false on a night
      already made up or still inside its makeup window - the standing reads
      the repair before it reads the state, so those were never in the
      denominator to leave.
    */
    return (
      <span className="basis-full text-good">
        &#10003; {excused ? 'Back in the count' : 'Taken out of the count'} &middot; this row reads right when the page reloads
      </span>
    )
  }

  if (excused) {
    return (
      <span className="flex basis-full flex-wrap items-baseline gap-x-2 text-ink-3">
        <button
          type="button"
          onClick={() => run('restore')}
          disabled={state === 'saving'}
          className="min-h-[24px] rounded-lg border border-line-2 px-2 py-0.5 text-[12px] font-semibold text-ink-3 hover:bg-surface-2 disabled:opacity-60"
        >
          {state === 'saving' ? 'Putting it back…' : 'Put it back in the count'}
        </button>

        {problem && (
          <span className="basis-full text-crit" role="alert">
            {problem}
          </span>
        )}
      </span>
    )
  }

  return (
    <span className="flex basis-full flex-wrap items-baseline gap-x-2 gap-y-1 text-ink-3">
      {open ? (
        <>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why, for the studio's record (optional)"
            aria-label="Why this one should not count"
            className="min-h-[24px] min-w-0 flex-1 rounded-lg border border-line-2 bg-surface px-2 py-1 text-[13px] text-ink focus:border-accent"
          />
          <button
            type="button"
            onClick={() => run('excuse')}
            disabled={state === 'saving'}
            className="min-h-[24px] rounded-lg border border-accent px-2 py-0.5 text-[12px] font-semibold text-accent hover:bg-accent-soft disabled:opacity-60"
          >
            {state === 'saving' ? 'Saving…' : "Don't count this"}
          </button>
          {/* A way out that is not a reload, as the other excuse screen has. */}
          <button
            type="button"
            onClick={() => { setOpen(false); setNote(''); setProblem(null) }}
            className="min-h-[24px] rounded-lg px-2 py-0.5 text-[12px] font-semibold text-ink-3 hover:bg-surface-2"
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          {/*
            ⚠ **The same words as /admin/absences** (N8): one act with two names
            is how two screens drift. That one also says what it does before she
            presses it, which is the half this was missing.
          */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="min-h-[24px] rounded-lg border border-line-2 px-2 py-0.5 text-[12px] font-semibold text-ink-3 hover:bg-surface-2"
          >
            Don&rsquo;t count this one
          </button>
          <span className="text-[12px]">Stops this session counting toward her attendance.</span>
        </>
      )}

      {problem && (
        <span className="basis-full text-crit" role="alert">
          {problem}
        </span>
      )}
    </span>
  )
}

function NearlyCoveredLine({
  nearly,
}: {
  nearly: NonNullable<MissedSession['nearlyCovered']>
}) {
  const [state, setState] = useState<'idle' | 'asking' | 'asked' | 'failed'>('idle')
  const [problem, setProblem] = useState<string | null>(null)

  async function ask() {
    setState('asking')
    setProblem(null)

    try {
      await raiseAsk({
        kind: 'banked-level',
        subject: { type: 'BankedNight', id: nearly.absenceRecordId },

        /*
         * **The credit this screen actually drew**, which the server checks
         * against its own answer before it will raise anything (D-935). It is
         * not trusted — naming a different one is refused — it is how her yes
         * gets pinned to the class she was shown.
         */
        content: nearly.creditOccurrenceId,
      })

      setState('asked')
    } catch (e) {
      setState('failed')
      setProblem(e instanceof Error ? e.message : 'That could not be sent.')
    }
  }

  // The server's answer, or this tap if the server has not been re-read yet.
  // Both, because a successful press should say so before the next fetch.
  const waiting = nearly.answer?.state === 'Waiting' || state === 'asked'
  const declined = nearly.answer?.state === 'Declined' ? nearly.answer : null

  // The same formatter the rest of the panel dates things with.
  const declinedOn = declined?.on ? formatDayLabel(declined.on) : null

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 text-ink-3">
      <span>
        A banked class nearly covered this &middot; {nearly.className} is {nearly.creditLevels},
        this night is {nearly.nightLevels}
      </span>

      {waiting ? (
        // The teacher has it now. Said rather than left blank, because a
        // button that simply vanishes reads as a tap that did nothing.
        //
        // ⚠ **From the server since D-938, not only from this tap.** It used to
        // be local state, so a refresh put the button back and the second press
        // rendered `Duplicate` as a red alert - the first thing the screen said
        // about its own successful ask was an error.
        //
        // ⚠ **"the teacher", not "her teacher"** - the owner's standing
        // correction, because not every dancer here is a girl. The line above
        // names the night, so which teacher is never in doubt.
        /*
          ⚠ **Not `describeClass(...).who`**, which an audit caught here.
          That field is who a solo, duet or trio is *for* — the **dancers** —
          so on a technique class it is always null and the fallback always
          fires, and on the one class where it is set the line would have read
          *"Asked Emma Lainey"* about the dancer herself.
        */
        <span className="text-good">&#10003; Asked the teacher</span>
      ) : (
        <>
          {/*
            ⚠ **Her refusal, which was recorded all along and shown to nobody**
            (D-938). `ReasonRequiredOnNo` means she had to type one and the
            family is emailed it - so the studio was the only party asking
            again without knowing she had already answered.

            **Asking again is still offered**, because the envelope allows it
            on purpose (D-937): a bare no is ambiguous between *not yet* and
            *not going to*. What changed is that it now costs a deliberate
            second press, under her words.
          */}
          {/*
            ⚠ **Not "The teacher" when the name is missing.** This kind
            escalates to `ViewAllSchedules` by design (D-936), so a studio
            reader answering is a built path - and the name is null for anybody
            with no staff link. Saying *the teacher said no* would then put a
            refusal in her mouth that she never gave.
          */}
          {declined && (
            <span className="basis-full text-ink-3">
              {declined.teacherName ?? 'Somebody at the studio'} said no
              {declinedOn ? ` on ${declinedOn.day} ${declinedOn.month}` : ''}
              {/* Entities, not literal curly quotes: this file has been
                  silently re-encoded to ANSI more than once, and mojibake is
                  valid TSX. */}
              {declined.reason ? <>: &ldquo;{declined.reason}&rdquo;</> : '.'}
            </span>
          )}

          <button
            type="button"
            onClick={ask}
            disabled={state === 'asking'}
            className={
              declined
                ? 'min-h-[24px] rounded-lg border border-line-2 px-2 py-0.5 text-[12px] font-semibold text-ink-3 hover:bg-surface-2 disabled:opacity-60'
                : 'min-h-[24px] rounded-lg border border-accent px-2 py-0.5 text-[12px] font-semibold text-accent hover:bg-accent-soft disabled:opacity-60'
            }
          >
            {state === 'asking' ? 'Asking…' : declined ? 'Ask again' : 'Ask the teacher'}
          </button>
        </>
      )}

      {problem && (
        <span className="basis-full text-crit" role="alert">
          {problem}
        </span>
      )}
    </span>
  )
}
